import { sanitizeText, sanitizeAmount, safeForExport } from "./security";

export const ACTIVITY_RETENTION_DAYS = 30;
export const ACTIVITY_RETENTION_MS = ACTIVITY_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const SAFE_ACTIONS = [
  "entry_created",
  "entry_updated",
  "entry_deleted",
  "profile_created",
  "profile_updated",
  "profile_deleted",
  "export_pdf",
  "export_excel",
  "export_json",
  "export_encrypted_backup",
  "export_activity_log"
];

const CHANGE_KEYS = [
  "amount", "type", "txType", "category", "status", "date", "dueDate",
  "profitAmount", "cashback", "grams", "pricePerGram", "platform", "metalType",
  "depositType", "bankName", "depositName", "interestRate", "maturityDate",
  "expectedMaturityAmount", "actualReturnedAmount", "penalty", "symbol",
  "assetType", "exchange", "qty", "buyPrice", "stopLoss", "target",
  "name", "profileType", "phone", "color", "icon"
];

function nowIso() {
  return new Date().toISOString();
}

export function activityCutoffMs(now = Date.now()) {
  return now - ACTIVITY_RETENTION_MS;
}

export function getActivityTimestamp(log = {}) {
  if (Number.isFinite(Number(log.createdAtMs))) return Number(log.createdAtMs);
  const parsed = Date.parse(String(log.createdAt || ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function pruneActivityLogs(logs = [], now = Date.now()) {
  const cutoff = activityCutoffMs(now);
  return (Array.isArray(logs) ? logs : [])
    .filter(log => getActivityTimestamp(log) >= cutoff)
    .sort((a, b) => getActivityTimestamp(b) - getActivityTimestamp(a))
    .slice(0, 1000);
}

function changedFields(before = {}, after = {}) {
  return CHANGE_KEYS.filter(key => {
    const a = before?.[key];
    const b = after?.[key];
    return JSON.stringify(a ?? "") !== JSON.stringify(b ?? "");
  }).slice(0, 24);
}

function summarizeAction(action = "") {
  const labels = {
    entry_created: "Entry created",
    entry_updated: "Entry updated",
    entry_deleted: "Entry deleted",
    profile_created: "Profile created",
    profile_updated: "Profile updated",
    profile_deleted: "Profile deleted",
    export_pdf: "PDF export downloaded",
    export_excel: "Excel export downloaded",
    export_json: "JSON backup downloaded",
    export_encrypted_backup: "Encrypted backup downloaded",
    export_activity_log: "Activity log downloaded"
  };
  return labels[action] || "Activity recorded";
}

export function createActivityLog({
  action,
  account,
  tx,
  before,
  after,
  entityType = "entry",
  message = ""
} = {}) {
  const effectiveAction = SAFE_ACTIONS.includes(action) ? action : "entry_updated";
  const source = after || tx || before || {};
  const diff = before && after ? changedFields(before, after) : [];
  const noteChanged = Boolean(before && after && String(before.note || "") !== String(after.note || ""));
  const amount = sanitizeAmount(source.amount || source.profitAmount || source.cashback || 0);

  return sanitizeActivityLog({
    action: effectiveAction,
    entityType,
    createdAt: nowIso(),
    createdAtMs: Date.now(),
    accountId: sanitizeText(account?.id || source.accountId || "", 120),
    profileName: sanitizeText(account?.name || source.profileName || "", 80),
    profileType: sanitizeText(account?.profileType || source.profileType || "", 30),
    entryId: sanitizeText(source.id || source.entryId || "", 120),
    txType: sanitizeText(source.txType || source.type || "", 40),
    category: sanitizeText(source.category || source.depositType || source.metalType || source.assetType || "", 50),
    amount,
    status: sanitizeText(source.status || "", 40),
    changedFields: diff,
    noteChanged,
    message: sanitizeText(message || summarizeAction(effectiveAction), 220)
  });
}

export function sanitizeActivityLog(log = {}) {
  const action = SAFE_ACTIONS.includes(log.action) ? log.action : "entry_updated";
  const createdAtMs = Number.isFinite(Number(log.createdAtMs)) ? Number(log.createdAtMs) : Date.now();
  const rawChanged = Array.isArray(log.changedFields) ? log.changedFields : [];
  return {
    id: sanitizeText(log.id || "", 120),
    action,
    entityType: sanitizeText(log.entityType || "entry", 30),
    createdAt: sanitizeText(log.createdAt || nowIso(), 40),
    createdAtMs,
    accountId: sanitizeText(log.accountId || "", 120),
    profileName: sanitizeText(log.profileName || "", 80),
    profileType: sanitizeText(log.profileType || "", 30),
    entryId: sanitizeText(log.entryId || "", 120),
    txType: sanitizeText(log.txType || "", 40),
    category: sanitizeText(log.category || "", 50),
    amount: sanitizeAmount(log.amount || 0),
    status: sanitizeText(log.status || "", 40),
    changedFields: rawChanged.map(v => sanitizeText(v, 40)).filter(Boolean).slice(0, 24),
    noteChanged: Boolean(log.noteChanged),
    message: sanitizeText(log.message || summarizeAction(action), 220)
  };
}

export function activityLogExportRows(accounts = [], activityLogMap = {}) {
  const accountById = Object.fromEntries((accounts || []).map(a => [a.id, a]));
  const rows = [];
  Object.entries(activityLogMap || {}).forEach(([accountId, logs]) => {
    const account = accountById[accountId] || {};
    (logs || []).forEach(log => {
      const safe = sanitizeActivityLog(log);
      rows.push({
        DateTime: safeForExport(safe.createdAt),
        Action: safeForExport(safe.action),
        Profile: safeForExport(safe.profileName || account.name || ""),
        ProfileType: safeForExport(safe.profileType || account.profileType || ""),
        Category: safeForExport(safe.category || safe.txType || ""),
        Amount: safe.amount,
        Status: safeForExport(safe.status),
        ChangedFields: safeForExport((safe.changedFields || []).join(", ")),
        NoteChanged: safe.noteChanged ? "Yes" : "No",
        Summary: safeForExport(safe.message),
        EntryId: safeForExport(safe.entryId)
      });
    });
  });
  return rows.sort((a, b) => Date.parse(b.DateTime || "") - Date.parse(a.DateTime || ""));
}
